// src/components/Experiments/ExperimentCard.jsx
// step5: единственное изменение — formatDate вынесен в src/utils/date.js
// (используется теперь и в ExperimentCard, и в новом ExperimentRow).
// Остальной файл не менялся.
import React, { useState, useMemo } from 'react';
import { Card, Badge, Spinner, Alert, Carousel } from 'react-bootstrap';
import useImageWithAuth from '../../hooks/useImageWithAuth';
import { formatDate } from '../../utils/date';

const JobThumbnail = ({ thumbnailUrl, index, total, isCarousel = false }) => {
  const [image, imageStatus, imageError] = useImageWithAuth('photo_processing', thumbnailUrl);
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    setHasError(true);
  };

  if (hasError || imageError) {
    return (
      <div className={`d-flex justify-content-center align-items-center ${isCarousel ? 'h-100 w-100' : 'h-100'}`}>
        <i className="bi bi-image text-muted" style={{ fontSize: '1.5rem' }}></i>
      </div>
    );
  }

  if (imageStatus === 'loading') {
    return (
      <div className={`d-flex justify-content-center align-items-center ${isCarousel ? 'h-100 w-100' : 'h-100'}`}>
        <Spinner animation="border" size="sm" variant="secondary" />
      </div>
    );
  }

  if (imageStatus === 'loaded' && image) {
    return (
      <img
        src={image.src}
        alt={`Job ${index + 1}`}
        className={`${isCarousel ? 'carousel-image' : 'grid-image'}`}
        style={{
          objectFit: 'cover',
          width: '100%',
          height: '100%',
        }}
        onError={handleError}
      />
    );
  }

  return null;
};

const ExperimentCard = ({
  experiment,
  onClick,
  selectionMode = false,
  isSelected = false,
  onToggleSelect,
  isRefreshing = false,
  showOwnerBadge = true,
  showGroupBadges = false,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Sharing system props
  const isOwner = experiment.is_owner ?? true;
  const groups = experiment.shared_groups ?? [];

  // Extract job thumbnails from selected_jobs
  const jobThumbnails = useMemo(() => {
    if (!experiment.selected_jobs || !Array.isArray(experiment.selected_jobs)) {
      return [];
    }

    return experiment.selected_jobs
      .filter(job => job && job.thumbnail)
      .map(job => job.thumbnail);
  }, [experiment.selected_jobs]);

  const handleClick = (e) => {
    if (selectionMode && onToggleSelect) {
      e.stopPropagation();
      onToggleSelect();
    } else if (onClick) {
      onClick();
    }
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (onToggleSelect) {
      onToggleSelect();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (selectionMode && onToggleSelect) {
        onToggleSelect();
      } else if (onClick) {
        onClick();
      }
    }
  };

  const renderThumbnailContent = () => {
    if (jobThumbnails.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center h-100 bg-light">
          <i className="bi bi-image text-muted" style={{ fontSize: '3rem' }}></i>
        </div>
      );
    }

    if (jobThumbnails.length <= 4) {
      // Grid layout for 1-4 images
      return (
        <div className="h-100 w-100">
          <div className={`h-100 grid-layout grid-${jobThumbnails.length}`}>
            {jobThumbnails.map((thumbnail, index) => (
              <div key={index} className="grid-item">
                <JobThumbnail
                  thumbnailUrl={thumbnail}
                  index={index}
                  total={jobThumbnails.length}
                />
              </div>
            ))}
          </div>
        </div>
      );
    } else {
      // Carousel for more than 4 images
      return (
        <Carousel
          activeIndex={currentImageIndex}
          onSelect={setCurrentImageIndex}
          indicators={false}
          controls={jobThumbnails.length > 1}
          interval={null}
          className="h-100"
        >
          {jobThumbnails.map((thumbnail, index) => (
            <Carousel.Item key={index} className="h-100">
              <JobThumbnail
                thumbnailUrl={thumbnail}
                index={index}
                isCarousel={true}
              />
            </Carousel.Item>
          ))}
        </Carousel>
      );
    }
  };

  return (
    <div
      className={`experiment-card-wrapper ${isSelected ? 'exp-card--selected' : ''} ${selectionMode ? 'exp-card--selectable' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={selectionMode ? 'checkbox' : 'button'}
      aria-checked={selectionMode ? isSelected : undefined}
      tabIndex={0}
      style={{ cursor: selectionMode ? 'pointer' : 'default', height: '100%' }}
    >
      <Card
        className={`experiment-card h-100 ${isSelected ? 'border-primary selected' : ''} ${selectionMode ? 'selectable' : ''}`}
        style={{ cursor: selectionMode ? 'pointer' : 'default', marginBottom: 0 }}
      >
        {/* Selection checkbox overlay (new style for sharing) */}
        {selectionMode && (
          <div className="exp-card__checkbox position-absolute top-0 end-0 m-2 z-2">
            {isSelected ? (
              <i className="bi bi-check-circle-fill text-primary" style={{ fontSize: '1.2rem' }} />
            ) : (
              <i className="bi bi-circle text-muted" style={{ fontSize: '1.2rem' }} />
            )}
          </div>
        )}

        {/* Owner / shared badge */}
        {showOwnerBadge && !isOwner && (
          <div className="exp-card__shared-badge position-absolute top-0 start-0 m-2 z-2">
            <i className="bi bi-share-fill me-1" />
            Shared
          </div>
        )}

        {/* Thumbnail section */}
        <div className="position-relative" style={{ height: '200px', overflow: 'hidden' }}>
          {renderThumbnailContent()}

          {isRefreshing && (
            <div className="position-absolute top-0 end-0 m-2 z-2">
              <Spinner animation="border" size="sm" variant="primary" />
            </div>
          )}

          {/* Jobs count badge (moved to avoid overlap with sharing badges) */}
          {!showOwnerBadge && (
            <Badge
              bg="secondary"
              className="position-absolute top-0 end-0 m-2 z-2"
            >
              {experiment.selected_jobs?.length || 0} jobs
            </Badge>
          )}

          {/* Jobs count badge when owner badge is shown (position adjusted) */}
          {showOwnerBadge && !isOwner && (
            <Badge
              bg="secondary"
              className="position-absolute top-0 end-0 m-2 z-2"
              style={{ marginTop: '2.5rem' }}
            >
              {experiment.selected_jobs?.length || 0} jobs
            </Badge>
          )}

          {/* Jobs count badge for owner or when owner badge not shown */}
          {((showOwnerBadge && isOwner) || !showOwnerBadge) && (
            <Badge
              bg="secondary"
              className="position-absolute top-0 end-0 m-2 z-2"
            >
              {experiment.selected_jobs?.length || 0} jobs
            </Badge>
          )}

          {/* Carousel indicator for multiple images */}
          {jobThumbnails.length > 4 && (
            <Badge
              bg="dark"
              className="position-absolute bottom-0 start-0 m-2 z-2"
            >
              {currentImageIndex + 1}/{jobThumbnails.length}
            </Badge>
          )}
        </div>

        <Card.Body className="d-flex flex-column">
          {/* Experiment name */}
          <Card.Title
              className="h6"
              title={experiment.name}
              style={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                lineHeight: '1.3',
              }}
          >
            {experiment.name}
          </Card.Title>

          {/* Description */}
          {experiment.description && (
            <Card.Text className="text-muted small flex-grow-1" style={{ fontSize: '0.875rem' }}>
              {experiment.description.length > 100
                ? `${experiment.description.substring(0, 100)}...`
                : experiment.description
              }
            </Card.Text>
          )}

          {/* Metadata */}
          <div className="mt-auto pt-2">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                {formatDate(experiment.created_at)}
              </small>

              {/* Plate schema info */}
              {experiment.plate_schema && (
                <Badge bg="outline-primary" text="dark" className="border">
                  {experiment.plate_schema.rows}x{experiment.plate_schema.columns}
                </Badge>
              )}
            </div>
          </div>
        </Card.Body>

        {/* Group chips (shown in All / Mine tabs) */}
        {showGroupBadges && groups.length > 0 && (
          <div className="exp-card__groups">
            {groups.slice(0, 3).map(g => (
              <span key={g.id} className="exp-card__group-chip" title={g.name}>
                <i className="bi bi-people-fill me-1" />
                {g.name}
              </span>
            ))}
            {groups.length > 3 && (
              <span className="exp-card__group-chip exp-card__group-chip--more">
                +{groups.length - 3}
              </span>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ExperimentCard;
